import { Button, Modal, ModalActions, ModalContent, ModalWrapper, Typography } from '@braskai/ui-kit';

import { Stack } from '@mui/material';

import { useExecuteOneTime } from '@/shared/hooks/useExecuteOneTime';
import { useInterfaceLanguage } from '@/shared/i18n';
import { formatInterfaceCurrency, formatInterfaceNumber } from '@/shared/i18n/formatLocale';
import { GTMEvents } from '@/shared/thirdParty/gtm';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { ModalHeaderDefault } from '@/shared/ui/modal';

import { getBuyMinutesViewItemListEventData } from '../lib/getBuyMinutesViewItemListEventData';
import { useBuyMinutesModalStore } from '../model/useBuyMinutesModalStore';
import { useMinutesBuying } from '../model/useMinutesBuying';
import { usePrice } from '../model/usePrice';
import { CreditSlider } from './CreditSlider';
import { UpgradeCard } from './UpgradeCard';

const BuyMinutesModalComponent = () => {
  const { locale, t } = useInterfaceLanguage();
  const onClose = useBuyMinutesModalStore((state) => state.close);
  const { price, pricePerMin, product, totalMinutes, handleChangeMinutes } = usePrice();
  const { checkout, isStripeCheckoutLoading } = useMinutesBuying(totalMinutes, price);
  const currency = product?.prices[0]?.currency ?? 'usd';
  const formattedUnitPrice = formatInterfaceCurrency(locale, pricePerMin * 100, currency);
  const formattedPrice = formatInterfaceCurrency(locale, price * 100, currency);

  const handleClose = () => {
    if (!isStripeCheckoutLoading) {
      onClose();
    }
  };

  useExecuteOneTime(() => {
    if (!product) {
      return;
    }

    MixpanelEvents.buyMinutesModalShown();
    const buyMinutesGtmData = getBuyMinutesViewItemListEventData({ price: pricePerMin, productId: product.id });
    GTMEvents.viewItemList(buyMinutesGtmData);
  });

  return (
    <Modal size="xs" open onClose={handleClose}>
      <ModalHeaderDefault title={t('pricing.buyMinutes.title')} onClose={handleClose} />

      <ModalContent noPadding>
        <Stack gap={16}>
          <CreditSlider currency={currency} pricePerMin={pricePerMin} onChange={handleChangeMinutes} />

          {pricePerMin > 1 ? <UpgradeCard onClose={onClose} /> : null}

          <Stack alignItems="center" direction="row" justifyContent="space-between">
            <Stack>
              <Typography variant="body-2-md">{t('pricing.buyMinutes.price')}</Typography>
              <Typography color="grey.500" variant="title-4-md">
                {formattedUnitPrice}&times;{formatInterfaceNumber(locale, totalMinutes)}
              </Typography>
            </Stack>
            <Typography variant="title-1-sb">{formattedPrice}</Typography>
          </Stack>
        </Stack>
      </ModalContent>

      <ModalActions>
        <Button
          data-testid="buy-minutes-modal-checkout-button"
          disabled={!price || isStripeCheckoutLoading}
          isLoading={isStripeCheckoutLoading}
          size="lg"
          fullWidth
          onClick={checkout}
        >
          {t('pricing.buyMinutes.checkout')}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export const BuyMinutesModal = () => (
  <ModalWrapper hook={useBuyMinutesModalStore}>
    <BuyMinutesModalComponent />
  </ModalWrapper>
);
